import { useState } from 'react';
import { t, getLocale } from '../i18n';

const faqs = {
  en: [
    {
      q: 'How does the matching work?',
      a: 'Each week, <em>match-a</em> looks at all members who have marked availability for the coming days and finds pairs (or small groups) that share at least one industry interest and one language. The algorithm avoids repeating past matches.'
    },
    {
      q: 'How often are matches made?',
      a: 'Matches are made once a week, every Monday at noon, for the following week.'
    },
    {
      q: 'What do I need to do to get matched?',
      a: 'Make sure your profile is complete (industry, languages, group size preference) and that you have marked at least one available day on the Availability calendar for the week ahead. Your availability must be set before noon on Monday — that is when the matching runs.'
    },
    {
      q: 'Privacy notice — what information is shared?',
      a: 'When you are matched, your name and email address are shared with your lunch partner so you can coordinate the meeting. No other personal information is disclosed. By using this service you consent to this sharing, in accordance with Japan\'s Act on the Protection of Personal Information (個人情報の保護に関する法律). Your data will not be used for any other purpose or passed to third parties beyond your match.'
    },
    {
      q: 'How do I communicate with my match?',
      a: 'You have two options: you can use the built-in chat on this platform (visible after a match is confirmed), or you can contact your match directly by email — their address is shared with you at the time of matching.'
    },
    {
      q: 'Can I be matched with the same person twice?',
      a: 'No. <em>match-a</em> keeps a record of past matches and will not pair you with the same person again.'
    },
    {
      q: 'What if I can no longer make the suggested lunch date?',
      a: 'Reach out to your match directly by email as soon as possible to reschedule or cancel. Remember to update your availability calendar so future matching reflects your real schedule.'
    },
    {
      q: 'How do I change my preferences or availability?',
      a: 'You can update your industry interests, languages, and group size preference at any time from the Profile page. Availability is managed separately on the Availability page.'
    },
    {
      q: 'Who do I contact if I have a problem?',
      a: 'Please send an email to inquiry-trial@match-a.net and the team will get back to you.'
    },
    {
      q: 'How do I delete my profile?',
      a: 'Go to Profile → Password, then scroll to the "Danger Zone" section at the bottom and click "Delete My Account". You will be asked to confirm your password (unless you signed in via Google/Microsoft) and confirm the deletion. This permanently removes your profile, preferences, and availability, and cannot be undone. Any lunch groups you were part of remain visible to your former matches, but your name will show as "Deleted User".'
    }
  ],
  ja: [
    {
      q: 'マッチングはどのように行われますか？',
      a: '毎週、<em>match-a</em>は翌日以降の空き時間を設定しているメンバーを確認し、少なくとも1つの業界と1つの言語が共通するペア（または小グループ）を見つけます。過去のマッチングは繰り返されません。'
    },
    {
      q: 'マッチングはどのくらいの頻度で行われますか？',
      a: 'マッチングは毎週月曜日の正午に1回実施され、翌週分のランチが対象となります。'
    },
    {
      q: 'マッチングされるためには何が必要ですか？',
      a: 'プロフィール（業界・言語・グループサイズ）を完成させ、空き時間カレンダーで翌週のランチ可能日を少なくとも1日設定してください。マッチングは月曜日の正午に実施されるため、それまでに空き時間を設定しておく必要があります。'
    },
    {
      q: 'プライバシーについて — どんな情報が共有されますか？',
      a: 'マッチングが成立すると、ランチの調整のために氏名とメールアドレスがマッチング相手に共有されます。その他の個人情報は開示されません。本サービスをご利用いただくことで、個人情報の保護に関する法律に基づき、この情報共有に同意したものとみなされます。お客様のデータは上記以外の目的には使用されず、マッチング相手以外の第三者に提供されることもありません。'
    },
    {
      q: 'マッチング相手とはどのように連絡を取ればよいですか？',
      a: '2つの方法があります。プラットフォーム上の内蔵チャット機能（マッチング確定後に利用可能）を使うか、マッチング時に共有されるメールアドレスに直接ご連絡ください。'
    },
    {
      q: '同じ人と再びマッチングされることはありますか？',
      a: '<em>match-a</em>は過去のマッチングを記録していますので、同じ相手と再びペアになることはありません。'
    },
    {
      q: '提案されたランチ日程に参加できなくなった場合はどうすればよいですか？',
      a: 'できるだけ早くマッチング相手にメールで連絡し、日程の変更またはキャンセルをお知らせください。また、今後のマッチングに反映されるよう、空き時間カレンダーを更新してください。'
    },
    {
      q: '希望条件や空き時間を変更するにはどうすればよいですか？',
      a: '業界、エリア、言語、グループサイズの設定はプロフィールページからいつでも変更可能です。ランチ可能日については「空き時間」のページから管理ができます。'
    },
    {
      q: '問題が発生した場合、誰に連絡すればよいですか？',
      a: 'inquiry-trial@match-a.net までメールでご連絡ください。チームが対応いたします。'
    },
    {
      q: 'プロフィールを削除するにはどうすればよいですか？',
      a: 'プロフィール→パスワードのタブを開き、下部の「危険な操作」セクションにある「アカウントを削除」をクリックしてください。パスワードの確認（Google/Microsoftでログインしている場合を除く）と削除の確認を求められます。この操作によりプロフィール、設定、空き時間情報が完全に削除され、元に戻すことはできません。過去にマッチングしたランチグループの相手からは引き続き記録が見えますが、あなたの名前は「Deleted User」と表示されます。'
    }
  ]
};

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      style={{
        borderBottom: '1px solid var(--border)',
        cursor: 'pointer'
      }}
      onClick={() => setOpen(o => !o)}
    >
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '18px 0', gap: 16
      }}>
        <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--text)', lineHeight: 1.4 }}>{q}</span>
        <span style={{
          fontSize: 20, color: 'var(--match-a)', flexShrink: 0,
          transform: open ? 'rotate(45deg)' : 'none', transition: 'transform 0.15s'
        }}>+</span>
      </div>
      {open && (
        <p style={{
          margin: '0 0 18px', fontSize: 14, color: 'var(--text-muted)',
          lineHeight: 1.7, paddingRight: 32
        }} dangerouslySetInnerHTML={{ __html: a }} />
      )}
    </div>
  );
}

export default function FAQPage() {
  const locale = getLocale();
  const items = faqs[locale] || faqs.en;

  return (
    <div className="container" style={{ maxWidth: 680, paddingTop: 48, paddingBottom: 64 }}>
      <h1 style={{ fontSize: 28, marginBottom: 8 }}>{t('faqTitle')}</h1>
      <p style={{ color: 'var(--text-muted)', marginBottom: 40, fontSize: 15 }} dangerouslySetInnerHTML={{ __html: t('faqSubtitle') }} />
      <div className="card" style={{ padding: '0 28px' }}>
        {items.map((item, i) => (
          <FAQItem key={i} q={item.q} a={item.a} />
        ))}
      </div>
    </div>
  );
}
